// Stateful collector fed parsed hook_started/hook_response events. The real
// contract (research/claude-headless-contract.md §1) gives hook_started
// hook_event_name+hook_index+matcher, but hook_response carries ONLY
// hook_event_name+status/output/permissionDecision — no hook_index, no
// tool_use_id. Since hook_response never carries an index to correlate
// against, correlation is by (hook_event_name, arrival ordinal) for BOTH
// event kinds — the Nth hook_started for a given name pairs with the Nth
// hook_response for that same name, in arrival order. This is good enough
// for this phase's evidence table since within one attempt a given hook name
// fires in the order the pack invokes it (no concurrent same-name hooks).
import type { ParsedEvent } from "./stream-json-event-parser.js";

export interface HookOccurrence {
  hookEventName: string;
  hookIndex: number | undefined;
  matcher: string | undefined;
  started: boolean;
  responded: boolean;
  status: string | undefined;
  decision: string | undefined;
  reason: string | undefined;
  reasonCode: string | undefined;
  incomplete: boolean;
}

const AUTOPILOT_REASON_PREFIX = /^AUTOPILOT(\{.*?\})/;

// Deny reason strings prefixed `AUTOPILOT{...json...}` normalize to a
// `blocked` verdict with parsed code. Non-throwing on parse failure — falls
// back to treating the whole string as the raw reason.
function normalizeReason(output: unknown): {
  reason: string | undefined;
  reasonCode: string | undefined;
} {
  if (typeof output !== "string") {
    return {
      reason: output === undefined ? undefined : JSON.stringify(output),
      reasonCode: undefined,
    };
  }
  const match = output.match(AUTOPILOT_REASON_PREFIX);
  if (!match) return { reason: output, reasonCode: undefined };
  try {
    const parsed = JSON.parse(match[1] as string) as { code?: string };
    return { reason: output, reasonCode: parsed.code };
  } catch {
    return { reason: output, reasonCode: undefined };
  }
}

function occurrenceKey(hookEventName: string, ordinal: number): string {
  return `${hookEventName}#ord${ordinal}`;
}

export class HookLifecycleEvidenceCollector {
  private readonly occurrences = new Map<string, HookOccurrence>();
  private readonly startedOrdinalByName = new Map<string, number>();
  private readonly respondedOrdinalByName = new Map<string, number>();

  feed(event: ParsedEvent): void {
    if (event.kind === "hook_started") {
      const name = event.raw.hook_event_name;
      const ordinal = (this.startedOrdinalByName.get(name) ?? 0) + 1;
      this.startedOrdinalByName.set(name, ordinal);
      const key = occurrenceKey(name, ordinal);
      this.occurrences.set(key, {
        hookEventName: name,
        hookIndex: event.raw.hook_index,
        matcher: event.raw.matcher,
        started: true,
        responded: false,
        status: undefined,
        decision: undefined,
        reason: undefined,
        reasonCode: undefined,
        incomplete: true,
      });
      return;
    }

    if (event.kind === "hook_response") {
      const name = event.raw.hook_event_name;
      const ordinal = (this.respondedOrdinalByName.get(name) ?? 0) + 1;
      this.respondedOrdinalByName.set(name, ordinal);
      const key = occurrenceKey(name, ordinal);
      const existing = this.occurrences.get(key);
      const { reason, reasonCode } = normalizeReason(event.raw.output);
      if (existing) {
        existing.responded = true;
        existing.status = event.raw.status;
        existing.decision = event.raw.permissionDecision ?? existing.decision;
        existing.reason = reason;
        existing.reasonCode = reasonCode;
        existing.incomplete = false;
      } else {
        // Response with no matching start (unusual, but tolerate).
        this.occurrences.set(key, {
          hookEventName: name,
          hookIndex: undefined,
          matcher: undefined,
          started: false,
          responded: true,
          status: event.raw.status,
          decision: event.raw.permissionDecision,
          reason,
          reasonCode,
          incomplete: false,
        });
      }
    }
  }

  all(): HookOccurrence[] {
    return Array.from(this.occurrences.values());
  }

  // Which required hook names (by hook_event_name/matcher) never got a
  // hook_started at all.
  missingRequired(requiredHookNames: string[]): string[] {
    const seenNames = new Set(
      this.all()
        .filter((o) => o.started)
        .map((o) => o.hookEventName),
    );
    return requiredHookNames.filter((name) => !seenNames.has(name));
  }

  incompleteOccurrences(): HookOccurrence[] {
    return this.all().filter((o) => o.started && !o.responded);
  }
}
